import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge, ConfirmDialog, AudioFX } from '../ui/primitives';
import { courierOf, courierOfTitle, CourierKey } from '../ui/couriers';
import { useKiosk } from './StationApp';
import { jobsFrom, usePrint, type AddressCheckData, type PrintRunResult } from './PrintProgress';
import { API_URL } from '../config/api';

export type ActiveSession = {
  sessionId: string;
  orderId: string;
  orderName: string;
  order: any;
  // Przewoźnik zidentyfikowany przez UCS (identify) — źródło prawdy dla badge'a kuriera
  // („Tworzenie przesyłki" + lista paczek). Bez tego front zgadywał po zamówieniu i mylił
  // GoodSpeed/DHL z InPost (sygnał przewoźnika nie zawsze siedzi w tagach Shopify).
  carrier?: { pluginId?: string; serviceId?: string; title?: string | null } | null;
  // Kurier pod adres (`address`) vs paczkomat / punkt odbioru — liczy backend z usługi UCS.
  // Kontrola adresu po wydruku etykiety dotyczy tylko `address`.
  deliveryType?: 'address' | 'pickup_point' | null;
  scannedItems?: any[];
  // Sztuki wysłane wcześniejszą (np. ręczną, z panelu Shopify) realizacją. Backend przycina
  // `order.line_items` do tego, co zostało, a te pokazujemy tylko wyszarzone — nie do pakowania.
  fulfilledLineItems?: { id: number; sku: string | null; title: string; variant_title?: string | null; quantity: number }[];
  totalItems: number;
  scannedCount: number;
  packages: any[];
  currentPackage?: any;
  currentPackageTypeId?: number | null;
  sourceOrder?: any;
  // Sugestie prezentów dla tego zamówienia (ADR-0007), przepuszczone z UCS identify
  // przez backend pakowania. Opt-in: gdy darowizny wyłączone → { enabled:false }.
  // Budżet klienta i koszt produkcji liczone są po stronie UCS i NIE wracają przez API —
  // operator po prostu wybiera 1 produkt z listy.
  gifts?: {
    enabled: boolean;
    labelNote?: string;
    suggestions?: GiftSuggestion[];
  } | null;
  // Telefon i e-mail klienta z zamówienia (liczy backend). Brak = starsze API — bez przycisku kontaktu.
  contact?: CustomerContact | null;
};

type ContactPhoneSource = 'shipping' | 'order' | 'billing';

/** Kontakt z klientem — do telefonu, gdy czegoś brakuje albo nie da się wysłać zamówienia w całości. */
type CustomerContact = {
  name: string | null;
  // Bez duplikatów; `display` do przeczytania, `dial` do linku tel:.
  phones: { display: string; dial: string; source: ContactPhoneSource; name: string | null }[];
  email: string | null;
  city: string | null;
};

/** Produkt-prezent do wyboru (ADR-0007) — bez kosztów, tylko identyfikacja + nazwa. */
type GiftSuggestion = { variantId: string | null; sku: string | null; title: string };

type Item = {
  id: string | number;
  sku: string;
  name: string;
  variant_title?: string;
  quantity: number;
  packed: number;
};

type PkgType = { id: number; name: string; length?: number; width?: number; height?: number; weight?: number; is_active?: boolean; categoryIds?: string[] };

// category_ids kartonu z API: tablica (Postgres jsonb), string JSON (SQLite) lub null → string[].
function parseCatIds(raw: any): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw.trim()) {
    try { const a = JSON.parse(raw); return Array.isArray(a) ? a.map(String) : []; } catch { return []; }
  }
  return [];
}

// Normalizacja numeru zamówienia do porównań (np. "#1007" === "1007").
const normOrderName = (n: any): string => String(n == null ? '' : n).replace(/[^a-z0-9]/gi, '').toLowerCase();

// Kartony dozwolone dla kategorii zamówienia. categoryIds puste = karton uniwersalny (każda
// metoda). Brak rozpoznanej kategorii (null) → bez filtra. Gdy filtr nic nie zostawia (a są
// kartony) → pokaż wszystkie, by nie zablokować stacji (to przypadek złej konfiguracji).
function typesForCategory(types: PkgType[], categoryId: string | null): PkgType[] {
  if (!categoryId) return types;
  const f = types.filter((p) => !p.categoryIds || p.categoryIds.length === 0 || p.categoryIds.includes(categoryId));
  return f.length ? f : types;
}

function suggestType(count: number, types: PkgType[]): PkgType | undefined {
  if (!types.length) return undefined;
  const sorted = [...types].sort((a, b) => ((a.length || 0) * (a.width || 0) * (a.height || 0)) - ((b.length || 0) * (b.width || 0) * (b.height || 0)));
  if (count <= 3) return sorted[0];
  if (count <= 8) return sorted[Math.min(1, sorted.length - 1)];
  return sorted[Math.min(2, sorted.length - 1)];
}

function isInpostOrder(o: any): boolean {
  const sm = (o?.shipping_method || '').toLowerCase();
  return o?.delivery_info?.carrier === 'InPost' || sm.includes('inpost') || sm.includes('paczkomat');
}
function isDhlOrder(o: any): boolean {
  const sm = (o?.shipping_method || '').toLowerCase();
  return o?.delivery_info?.carrier === 'DHL' || sm.includes('dhl') || sm.includes('kurier');
}

/* ---- wybor typu paczki (asystent sugeruje rozmiar) ---- */
function PackageTypeSheet({ remaining, items, types, current, categoryName, loading, onPick, onCancel, cancelLabel }: { remaining: number; items: Item[]; types: PkgType[]; current?: PkgType | null; categoryName?: string; loading?: boolean; onPick: (p: PkgType) => void; onCancel: () => void; cancelLabel: string }) {
  // Dopóki nie znamy kategorii zamówienia i typów opakowań, NIE liczymy sugestii — inaczej
  // asystent podpowiadałby rozmiar ze wszystkich kartonów zamiast tylko pasujących do kategorii.
  const sug = loading ? undefined : suggestType(remaining, types);
  // W pickerze pokazujemy WYŁĄCZNIE to, co zostało do spakowania (ilość = zamówiona − spakowana),
  // pomijając pozycje już skompletowane — operator dobiera pudło pod to, co jeszcze do niego włoży.
  const toPack = items
    .map((it) => ({ ...it, remaining: it.quantity - it.packed }))
    .filter((it) => it.remaining > 0);
  const remainingQty = toPack.reduce((s, it) => s + it.remaining, 0);
  return (
    <div className="scrim" onClick={onCancel}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h3>{current ? 'Zmień typ paczki' : 'Wybierz typ paczki'}</h3>
          <button className="icon-btn" onClick={onCancel} title={cancelLabel} aria-label={cancelLabel}><Icon name="x" size={20} /></button>
        </div>
        {loading ? (
          // Kategoria/typy jeszcze się ustalają — spinner zamiast migotania pełną listą opakowań
          // (i sugestii liczonej ze wszystkich typów). „Anuluj" zostaje, by nie uwięzić operatora.
          <div className="ship-poll" style={{ padding: '28px 0 12px' }}>
            <div className="spinner" />
            <div style={{ fontWeight: 600, fontSize: 15 }}>Dobieranie opakowań do kategorii…</div>
          </div>
        ) : (
          <>
            {categoryName && (
              <div className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>
                Kategoria zamówienia: <b>{categoryName}</b> — pokazane tylko pasujące opakowania.
              </div>
            )}
            {toPack.length > 0 && (
              <div className="pkg-contents">
                <div className="pkg-contents-head">
                  Do spakowania<span>{remainingQty} szt.</span>
                </div>
                <div className="pkg-contents-list">
                  {toPack.map((it) => (
                    <div key={String(it.id)} className="pkg-content-row">
                      <span className="pcr-qty">{it.remaining}×</span>
                      <span className="pcr-name">{it.name}{it.variant_title ? ` · ${it.variant_title}` : ''}</span>
                      <span className="pcr-sku">{it.sku}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sug && (
              <div className="assist" style={{ margin: '0 0 16px' }}>
                <Icon name="sparkles" size={17} />
                <span>Asystent: sugerowany rozmiar paczki to <b>{sug.name}</b> ({remaining} szt. do spakowania).</span>
              </div>
            )}
            <div className="pkg-types">
              {types.map((p) => {
                const isCurrent = current?.id === p.id;
                const isSug = !current && p.id === sug?.id;
                return (
                  <button key={p.id} className={`pkg-type ${isCurrent ? 'selected' : ''} ${isSug ? 'suggested' : ''}`} onClick={() => { AudioFX.click(); onPick(p); }}>
                    {isCurrent ? <span className="pt-flag">wybrane</span> : isSug ? <span className="pt-flag">sugerowane</span> : null}
                    <div className="pt-name">{p.name}</div>
                    <div className="pt-dim">{p.length}×{p.width}×{p.height} cm · {p.weight} kg</div>
                  </button>
                );
              })}
            </div>
            {types.length === 0 && <div className="muted" style={{ fontSize: 14 }}>Brak typów paczek — dodaj je w Ustawieniach panelu admina.</div>}
          </>
        )}
        <button className="big-btn bb-ghost" style={{ marginTop: 16 }} onClick={onCancel}>{cancelLabel}</button>
      </div>
    </div>
  );
}

const PHONE_SOURCE_LABEL: Record<ContactPhoneSource, string> = {
  shipping: 'Odbiorca · adres dostawy',
  order: 'Zamawiający · telefon z zamówienia',
  billing: 'Płatnik · adres rozliczeniowy',
};

// Aplikacja PackFlow Station (Electron, pełny ekran): link tel:/mailto: otworzyłby systemowy wybór
// aplikacji nad kioskiem — tam nie ma ani „Zadzwoń", ani „Napisz".
function isStationKiosk(): boolean {
  try { return localStorage.getItem('packflow.kiosk') === '1'; } catch { return false; }
}

// „Zadzwoń" tylko na telefonie / tablecie: komputer nie ma czym zadzwonić, a tel: otwiera tam pusty
// wybór aplikacji. Po urządzeniu, nie po szerokości okna — telefon obrócony poziomo nadal dzwoni.
// iPad przedstawia się jak Mac, więc wypada z listy; i tak nie dzwoni.
function isPhoneOrTablet(): boolean {
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (nav.userAgentData?.mobile) return true;
  return /Android|iPhone|iPod|Mobile/i.test(nav.userAgent || '');
}

/* ---- kontakt z klientem: brakuje produktu / czegoś nie da się wysłać → telefon przed zamknięciem paczki ---- */
function ContactSheet({ contact, orderName, onClose }: { contact: CustomerContact; orderName: string; onClose: () => void }) {
  const kiosk = isStationKiosk();
  const canCall = !kiosk && isPhoneOrTablet();
  const canMail = !kiosk;
  const who = [contact.name, contact.city].filter(Boolean).join(' · ');
  const empty = contact.phones.length === 0 && !contact.email;
  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h3>Kontakt z klientem</h3>
          <button className="icon-btn" onClick={onClose} title="Zamknij" aria-label="Zamknij"><Icon name="x" size={20} /></button>
        </div>
        <div className="sub">Zamówienie <b>{orderName}</b>{who ? ` · ${who}` : ''}</div>
        <div className="assist" style={{ margin: '0 0 16px' }}>
          <Icon name="info" size={17} />
          <span>
            Brakuje produktu albo czegoś nie da się wysłać? Zadzwoń do klienta, zanim zamkniesz paczkę, i podaj
            numer zamówienia <b>{orderName}</b>.{canCall ? '' : ' Numer wybierz na telefonie.'}
          </span>
        </div>
        {empty ? (
          <div className="muted" style={{ fontSize: 15, margin: '4px 0 18px' }}>Klient nie podał w zamówieniu telefonu ani e-maila.</div>
        ) : (
          <div className="contact-list">
            {contact.phones.map((p) => (
              <div key={p.dial} className="contact-row">
                <div className="cr-main">
                  <div className="cr-label">{PHONE_SOURCE_LABEL[p.source]}</div>
                  {p.name && <div className="cr-name">{p.name}</div>}
                  <div className="cr-value">{p.display}</div>
                </div>
                {canCall && (
                  <a className="big-btn bb-primary" href={`tel:${p.dial}`} onClick={() => AudioFX.click()}>
                    <Icon name="phone" size={20} /> Zadzwoń
                  </a>
                )}
              </div>
            ))}
            {contact.phones.length === 0 && (
              <div className="muted" style={{ fontSize: 14 }}>Klient nie podał telefonu — zostaje e-mail.</div>
            )}
            {contact.email && (
              <div className="contact-row">
                <div className="cr-main">
                  <div className="cr-label">E-mail</div>
                  <div className="cr-value cr-email">{contact.email}</div>
                </div>
                {canMail && (
                  <a className="big-btn bb-ghost" href={`mailto:${contact.email}?subject=${encodeURIComponent(`Zamówienie ${orderName}`)}`} onClick={() => AudioFX.click()}>
                    <Icon name="mail" size={20} /> Napisz
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        <button className="big-btn bb-ghost" onClick={onClose}>Zamknij</button>
      </div>
    </div>
  );
}

// Surowa wartość pola adresu / notatki z zamówienia (string albo nic) — bez przycinania środka,
// bo kontrola adresu ma pokazać dokładnie to, co wpisał klient.
const rawText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/* ---- numeryczny pinpad SKU (alternatywa dla skanera; SKU są numeryczne) ---- */
function PinPad({ value, onChange, onConfirm, onClose }: {
  value: string; onChange: (v: string) => void; onConfirm: () => void; onClose: () => void;
}) {
  // onPointerDown + preventDefault: tap w klawisz nie przenosi fokusu (nie wyskakuje
  // pełna klawiatura ekranowa) i reaguje natychmiast pod palcem.
  const press = (fn: () => void) => (e: React.PointerEvent) => { e.preventDefault(); AudioFX.click(); fn(); };
  const append = (d: string) => onChange(value + d);
  return (
    <div className="pinpad-island" role="group" aria-label="Numeryczna klawiatura SKU">
      <div className="pinpad-head">
        <span className="pinpad-title">Wpisz SKU</span>
        <button type="button" className="icon-btn" onPointerDown={press(onClose)} aria-label="Zamknij klawiaturę numeryczną">
          <Icon name="chevron-down" size={20} />
        </button>
      </div>
      <div className="pinpad-display" aria-hidden="true">
        {value ? <span className="pinpad-val">{value}</span> : <span className="pinpad-ph">Numer SKU…</span>}
        <span className="pinpad-caret" />
      </div>
      <div className="pinpad-grid">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button type="button" key={d} className="pinpad-key" onPointerDown={press(() => append(d))}>{d}</button>
        ))}
        <button type="button" className="pinpad-key pinpad-fn" onPointerDown={press(() => onChange(value.slice(0, -1)))} aria-label="Usuń znak">
          <Icon name="delete" size={24} />
        </button>
        <button type="button" className="pinpad-key" onPointerDown={press(() => append('0'))}>0</button>
        <button type="button" className="pinpad-key pinpad-enter" onPointerDown={press(onConfirm)} aria-label="Zatwierdź SKU">
          <Icon name="corner-down-left" size={22} />
        </button>
      </div>
    </div>
  );
}

export default function SessionScreen() {
  const navigate = useNavigate();
  const { push, flash, compact, session, setSession } = useKiosk();
  const [items, setItems] = useState<Item[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [pkgType, setPkgType] = useState<PkgType | null>(null);
  const [openCount, setOpenCount] = useState(0); // pozycje w otwartej paczce
  const [scanVal, setScanVal] = useState('');
  const [pinpad, setPinpad] = useState(false);
  const [sheet, setSheet] = useState<null | 'pkgtype' | 'abort' | 'finish' | 'contact'>(null);
  const { startPrint, printing } = usePrint();
  const [variant, setVariant] = useState<'two-col' | 'focus'>('two-col');
  const [busy, setBusy] = useState(false);
  // Wybrany prezent (ADR-0007) — pojedynczy produkt („wybierz 1"). Prezent NIE jest pozycją
  // zamówienia (backend go nie skanuje), więc żyje tylko tu: po wyborze wskakuje na listę „do
  // spakowania" (giftPacked = operator dorzucił go do paczki) i przy finiszu trafia do rejestru
  // UCS. Bez kosztów — cenę produkcji rozwiązuje UCS po swojej stronie.
  const [giftPick, setGiftPick] = useState<GiftSuggestion | null>(null);
  const [giftPacked, setGiftPacked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // sesja zakonczona/przerwana z tego ekranu — blokuje efekt wznowienia,
  // ktory inaczej wygrywa wyscig z nawigacja (navigate jest transition)
  const endedRef = useRef(false);
  const order = session?.sourceOrder || session?.order;
  // Kurier dla arkusza „Tworzenie przesyłki" bierzemy z przewoźnika ZIDENTYFIKOWANEGO przez UCS
  // (session.carrier: inpost-shipx / dhl24-pl / goodspeed-manual …), a nie z heurystyki po
  // zamówieniu — sygnał przewoźnika nie zawsze jest w tagach Shopify, więc courierOf(order) mylił
  // GoodSpeed/DHL z InPost. Fallback na heurystykę zamówienia tylko, gdy UCS nic nie zwrócił.
  const shipmentCourier: CourierKey = session?.carrier
    ? courierOfTitle(session.carrier.title || session.carrier.pluginId || '')
    : courierOf(order || {});
  // GoodSpeed drukuje własną lokalną etykietę A6 zamiast etykiety z UCS (patrz backend).
  const shippingLabelTitle = /goodspeed/i.test(session?.carrier?.pluginId || '') ? 'Etykieta GoodSpeed' : 'Etykieta kurierska';

  // Kontrola adresu po wydruku etykiety — przełącznik w panelu (Ustawienia → Auto-druk). Bez
  // odpowiedzi (starsze API, błąd) po prostu jej nie pokazujemy: druk działa jak dotąd.
  const { data: printFlags } = useQuery<{ address_check_after_label?: boolean }>({
    queryKey: ['kiosk-print-flags'],
    queryFn: async () => (await axios.get(`${API_URL}/api/settings/printing`)).data,
    staleTime: 60000,
    retry: false,
  });
  const addressCheckOn = !!printFlags?.address_check_after_label && session?.deliveryType === 'address';

  // Surowe dane do kontroli adresu bierzemy z zamówienia Shopify z sesji (session.order), a nie z
  // `order`: przy starcie z kolejki to wiersz UCS bez linii adresu i bez notatki klienta.
  function addressCheckFor(packageNumber: number): AddressCheckData | null {
    if (!addressCheckOn || !session) return null;
    const ship = (session.order?.shipping_address || {}) as Record<string, unknown>;
    return {
      line1: rawText(ship.address1),
      line2: rawText(ship.address2),
      postalCode: rawText(ship.zip),
      city: rawText(ship.city),
      note: rawText(session.order?.note),
      orderName: session.orderName,
      packageNumber,
      courier: shipmentCourier,
    };
  }

  const { data: pkgTypes = [], isInitialLoading: pkgTypesLoading } = useQuery<PkgType[]>({
    queryKey: ['kiosk-pkg-types'],
    queryFn: async () => ((await axios.get(`${API_URL}/api/settings/package-types`)).data || [])
      .filter((p: PkgType) => p.is_active !== false)
      // category_ids wraca jako tablica (PG) lub string JSON (SQLite) — normalizujemy do string[].
      .map((p: any) => ({ ...p, categoryIds: parseCatIds(p.category_ids) })),
  });

  // Kategorię zamówienia najpewniej znamy OD RĘKI: OrdersScreen startując sesję przekazuje cały
  // wiersz kolejki jako sourceOrder, a ten niesie już { id, name }. Używamy jej wprost — bez
  // round-tripu i bez wyścigu, więc picker nie miga „wszystkimi" typami, zanim filtr wskoczy.
  const sourceCategory = (session?.sourceOrder?.category ?? null) as { id: string | null; name?: string } | null;

  // Fallback TYLKO dla wznowienia po odświeżeniu (sesja z /session/:id nie ma sourceOrder):
  // dociągamy kategorię z kolejki UCS i dopasowujemy po shopifyOrderId/numerze. Query włączamy
  // wyłącznie, gdy nie znamy kategorii z sourceOrder — inaczej zbędny request i wyścig. retry:false
  // + timeout: gdy UCS jest niedostępny, nie blokujemy stacji (po rozwiązaniu pokazujemy wszystkie).
  const { data: ucsQueue, isInitialLoading: queueLoading } = useQuery<any>({
    queryKey: ['kiosk-ucs-packages-now'],
    queryFn: async () => (await axios.get(`${API_URL}/api/ucs/packages/now`, { timeout: 8000 })).data,
    enabled: !sourceCategory,
    retry: false,
    staleTime: 60000,
  });
  // Kategoria zamówienia: sourceOrder (od ręki) > dopasowanie w kolejce UCS (po shopifyOrderId/numerze).
  const orderCategory = useMemo<{ id: string | null; name?: string } | null>(() => {
    if (sourceCategory) return sourceCategory;
    const pkgs = ucsQueue?.packages;
    if (!Array.isArray(pkgs) || !order) return null;
    const oid = order.id != null ? Number(order.id) : null;
    const oname = normOrderName(session?.orderName ?? order.name);
    const match = pkgs.find((p: any) =>
      (oid != null && Number(p.shopifyOrderId) === oid) || (oname && normOrderName(p.orderName) === oname),
    );
    return match?.category ?? null;
  }, [sourceCategory, ucsQueue, order, session]);

  // Picker trzymamy na spinnerze, dopóki realnie ustalamy kategorię (ścieżka wznowienia) lub
  // ładują się typy opakowań — wtedy nie pokazujemy pełnej listy ani nie liczymy sugestii ze
  // wszystkich kartonów. isInitialLoading jest false, gdy query jest wyłączone (kategoria z sourceOrder).
  const pickerLoading = queueLoading || pkgTypesLoading;

  // Typy opakowań ograniczone do kategorii zamówienia (jak picker w panelu admina).
  const visibleTypes = useMemo(() => typesForCategory(pkgTypes, orderCategory?.id ?? null), [pkgTypes, orderCategory]);

  /* init pozycji z sesji (+ wznowienie po odswiezeniu) */
  useEffect(() => {
    if (endedRef.current) return;
    if (session) {
      const lineItems: Item[] = (session.order?.line_items || []).map((li: any) => ({
        id: li.id,
        sku: li.sku || String(li.id),
        name: li.name || li.title,
        variant_title: li.variant_title,
        quantity: li.quantity,
        packed: 0,
      }));
      // Skany są liczone po SKU, a kilka wierszy może mieć ten sam SKU (warianty z jednym SKU) —
      // rozkładamy je po kolei na te wiersze, zamiast dobijać tylko pierwszy.
      for (const s of session.scannedItems || []) {
        let left = s.quantity || 1;
        for (const it of lineItems) {
          if (left <= 0) break;
          if (it.sku !== s.sku) continue;
          const take = Math.min(left, it.quantity - it.packed);
          it.packed += take;
          left -= take;
        }
      }
      setItems(lineItems);
      setPackages(session.packages || []);
      setOpenCount(session.currentPackage?.items?.length || 0);
      if (!session.currentPackage) setSheet('pkgtype');
      return;
    }
    // brak sesji w pamieci -> sprobuj wznowic z localStorage
    const saved = localStorage.getItem('currentPackingSession');
    if (!saved) { navigate('/packing-station/orders'); return; }
    const { sessionId, orderName } = JSON.parse(saved);
    axios.get(`${API_URL}/api/packing/session/${sessionId}`)
      .then((r) => {
        setSession({ sessionId, orderName, ...r.data });
        push('Wznowiono sesję po odświeżeniu', 'info');
      })
      .catch(() => {
        localStorage.removeItem('currentPackingSession');
        navigate('/packing-station/orders');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Gdy otwarty jest dowolny arkusz (wybór paczki / dialogi) albo ekran drukowania, odbierz fokus
  // polu skanowania — wybór odbywa się dotykiem, więc klawiatura ekranowa nie powinna
  // przesłaniać listy produktów ani typów paczek, a skan w trakcie druku nie może trafić do pola.
  // Pinpad też chowamy, by nie wisiał pod modalem.
  useEffect(() => {
    if (sheet || printing) { inputRef.current?.blur(); setPinpad(false); }
  }, [sheet, printing]);

  const orderNeeded = items.reduce((s, i) => s + i.quantity, 0);
  const orderPacked = items.reduce((s, i) => s + i.packed, 0);
  // Wybrany prezent liczy się jak jedna pozycja do spakowania — operator ma go fizycznie dorzucić
  // do paczki, więc wchodzi do postępu (np. 0/2 → 0/3) i czeka na „dotknij, aby spakować".
  const giftNeeded = giftPick ? 1 : 0;
  const giftPackedCount = giftPick && giftPacked ? 1 : 0;
  const totalNeeded = orderNeeded + giftNeeded;
  const totalPacked = orderPacked + giftPackedCount;
  const pct = totalNeeded ? Math.round((totalPacked / totalNeeded) * 100) : 0;
  const allPacked = totalNeeded > 0 && totalPacked >= totalNeeded;

  // Ile sztuk każdego SKU trafiło już do ZAMKNIĘTYCH paczek. Zamknięta paczka ma etykietę (numer
  // śledzenia) i jest nieodwracalna, więc tych sztuk NIE wolno odpiąć. To, co siedzi w bieżącej
  // otwartej paczce, to różnica: packed − zamknięte — i tylko tyle można cofnąć.
  const closedPackedBySku = useMemo(() => {
    const m = new Map<string, number>();
    for (const pkg of packages) {
      for (const it of pkg.items || []) {
        const sku = String(it.sku ?? '');
        m.set(sku, (m.get(sku) || 0) + (Number(it.quantity) || 0));
      }
    }
    return m;
  }, [packages]);

  const doScan = useCallback(async (code: string) => {
    if (!session) return;
    const norm = (code || '').trim();
    if (!norm) return;
    // Typ paczki MUSI być wybrany, zanim cokolwiek do niej trafi (jak w aplikacji Android): bez tego
    // produkt wpadłby do paczki bez opakowania, której potem nie da się otypować — operator utyka.
    // Brak typu → dźwięk + komunikat + otwarcie pickera zamiast dodawania do paczki bez opakowania.
    if (!pkgType) {
      AudioFX.buzz();
      flash('err', 'Najpierw wybierz typ opakowania');
      setSheet('pkgtype');
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/packing/scan`, { sessionId: session.sessionId, barcode: norm, quantity: 1 });
      if (r.data.success) {
        AudioFX.beep();
        const prodName = r.data.product?.name || norm;
        setItems((arr) => {
          const matches = (i: Item) => i.sku.toUpperCase() === norm.toUpperCase() || String(i.id) === norm;
          // Pierwszy niepełny wiersz z tym kodem — przy kilku wierszach o tym samym SKU kolejna
          // sztuka trafia do następnego, a nie ginie na już pełnym pierwszym.
          const open = arr.findIndex((i) => matches(i) && i.packed < i.quantity);
          const idx = open !== -1 ? open : arr.findIndex(matches);
          if (idx === -1) return arr;
          return arr.map((it, i) => (i === idx ? { ...it, packed: Math.min(it.quantity, it.packed + 1) } : it));
        });
        setOpenCount((n) => n + 1);
        flash('ok', 'Dodano ×1', prodName);
      } else {
        AudioFX.buzz();
        const err = r.data.error || 'Nieznany kod';
        if (err.includes('typ opakowania')) { setSheet('pkgtype'); push(err, 'info'); }
        else flash('err', err, norm);
      }
    } catch (e: any) {
      AudioFX.buzz();
      flash('err', e?.response?.data?.error || 'Błąd skanowania', norm);
    }
  }, [session, pkgType, flash, push]);

  function tapItem(it: Item) {
    if (it.packed >= it.quantity) { AudioFX.buzz(); flash('err', 'Pozycja już kompletna', it.name); return; }
    doScan(it.sku);
  }
  // Prezent nie przechodzi przez skaner backendu (nie jest pozycją zamówienia) — dotknięcie tylko
  // przełącza jego stan „spakowany" lokalnie, potwierdzając, że operator dorzucił go do paczki.
  function tapGift() {
    if (!giftPick) return;
    AudioFX.beep();
    setGiftPacked((p) => !p);
  }
  // Ile sztuk danej pozycji jest w bieżącej, OTWARTEJ paczce (a więc da się jeszcze odpiąć).
  // Sztuki w zamkniętych paczkach mają już etykietę i są nieodwracalne → nie liczą się tutaj.
  function openPackedOf(it: Item): number {
    return Math.max(0, it.packed - (closedPackedBySku.get(it.sku) || 0));
  }
  // Cofnięcie skanu (odpięcie) JEDNEJ sztuki z otwartej paczki. Dostępne tylko, gdy pozycja ma
  // sztuki w otwartej paczce — backend pilnuje tego samego (operuje wyłącznie na currentPackage).
  const doUnscan = useCallback(async (it: Item) => {
    if (!session || busy) return;
    if (it.packed - (closedPackedBySku.get(it.sku) || 0) <= 0) {
      AudioFX.buzz();
      flash('err', 'Pozycja jest w zamkniętej paczce', it.name);
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/packing/unscan`, { sessionId: session.sessionId, barcode: it.sku, quantity: 1 });
      if (r.data.success) {
        AudioFX.click();
        setItems((arr) => arr.map((x) => (String(x.id) === String(it.id) ? { ...x, packed: Math.max(0, x.packed - 1) } : x)));
        setOpenCount((n) => Math.max(0, n - 1));
        flash('ok', 'Cofnięto ×1', it.name);
      } else {
        AudioFX.buzz();
        flash('err', r.data.error || 'Nie udało się cofnąć', it.sku);
      }
    } catch (e: any) {
      AudioFX.buzz();
      flash('err', e?.response?.data?.error || 'Błąd cofania', it.sku);
    }
  }, [session, busy, closedPackedBySku, flash]);
  function scanNext() {
    const next = items.find((i) => i.packed < i.quantity);
    if (next) doScan(next.sku);
    else push('Wszystko zeskanowane', 'info');
  }
  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    doScan(scanVal);
    setScanVal('');
    // Na telefonie/tablecie (compact) wybór jest dotykowy — nie wymuszaj fokusu,
    // bo niepotrzebnie wyskakuje klawiatura ekranowa.
    if (!compact) inputRef.current?.focus();
  }
  // Pinpad: numeryczna alternatywa dla skanera. Otwarcie odbiera fokus polu, żeby
  // nie nakładała się na niego pełna klawiatura ekranowa (QWERTY).
  function openPinpad() {
    inputRef.current?.blur();
    setPinpad(true);
  }
  function pinpadConfirm() {
    if (!scanVal.trim()) return;
    doScan(scanVal);
    setScanVal('');
    // pinpad zostaje otwarty — operator wbija kolejne SKU bez ponownego otwierania.
  }

  async function pickType(p: PkgType) {
    if (!session) return;
    try {
      await axios.post(`${API_URL}/api/packing/select-package-type`, { sessionId: session.sessionId, packageTypeId: p.id });
      setPkgType(p);
      setSheet(null);
      push(`Opakowanie: ${p.name}`, 'info');
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Błąd wyboru opakowania';
      if (e?.response?.status === 404) {
        endedRef.current = true;
        localStorage.removeItem('currentPackingSession');
        push('Sesja wygasła — rozpocznij pakowanie ponownie', 'err');
        navigate('/packing-station/orders');
        setSession(null);
      } else push(msg, 'err');
    }
  }

  // Wynik zakupu / ponowienia etykiety paczki dla ekranu drukowania. Są zadania druku → ekran śledzi
  // ich status. Nie ma: druk padł przed kolejką (błąd z ponownym wydrukiem tej samej przesyłki) albo
  // auto-druk jest wyłączony — wtedy nie ma czego pokazywać i zostaje dotychczasowy toast (tak samo
  // przy starszym API, które nie zwraca id zadań).
  function parcelLabelResult(pkg: any): PrintRunResult {
    const jobIds: string[] = Array.isArray(pkg.labelJobIds) ? pkg.labelJobIds : [];
    const trackingNumber: string | null = pkg.trackingNumber || null;
    const base: PrintRunResult = {
      jobIds,
      trackingNumber,
      detail: trackingNumber ? `Nr przesyłki: ${trackingNumber}` : undefined,
      redo: () => reprintParcelLabel(pkg),
    };
    if (jobIds.length > 0) return base;
    if (pkg.labelError) {
      return { ...base, error: `Przesyłka utworzona, ale etykieta się nie wydrukowała: ${pkg.labelError}` };
    }
    AudioFX.success();
    push(trackingNumber ? `Tracking: ${trackingNumber}` : 'Paczka zamknięta', 'ok');
    return { ...base, silent: true };
  }

  // „Drukuj ponownie", gdy wydruk etykiety nie powstał (nie ma zadania do powtórzenia): backend
  // odtwarza zakup TEJ SAMEJ przesyłki stałym kluczem idempotencji i drukuje ją — niczego nie kupuje.
  async function reprintParcelLabel(pkg: any): Promise<PrintRunResult> {
    if (!session) throw new Error('Sesja pakowania wygasła');
    const r = await axios.post(`${API_URL}/api/packing/reprint-label`, {
      sessionId: session.sessionId,
      packageId: pkg.packageId,
    });
    const updated = r.data.package;
    setPackages((arr) => arr.map((p) => (p.packageId === pkg.packageId ? { ...p, ...updated } : p)));
    return parcelLabelResult({ ...pkg, ...updated });
  }

  /* zamkniecie paczki + przesylka kurierska (jedna etykieta NA PACZKĘ) */
  function closePackage() {
    if (!session) return;
    if (openCount === 0) { push('Zeskanuj kod lub dotknij pozycję', 'info'); return; }
    if (!pkgType) { setSheet('pkgtype'); return; }
    const type = pkgType;
    const count = openCount;
    const packageNumber = packages.length + 1;
    const itemsLeft = totalPacked < totalNeeded;
    let closed = false;
    // Etykietę (jedną na paczkę) kupuje backend wewnątrz complete-package przez UCS, na podstawie
    // wybranego kartonu — każda paczka to osobna przesyłka z własnym numerem śledzenia. To
    // wywołanie robi round-trip do kuriera, więc ekran drukowania zaczyna od kroku „Przesyłka".
    startPrint({
      title: shippingLabelTitle,
      subtitle: `${session.orderName} · Paczka ${packageNumber}`,
      preparing: 'Tworzenie przesyłki u przewoźnika…',
      firstStep: 'Przesyłka',
      courier: shipmentCourier,
      addressCheck: addressCheckFor(packageNumber),
      run: async () => {
        setBusy(true);
        try {
          const r = await axios.post(`${API_URL}/api/packing/complete-package`, {
            sessionId: session.sessionId,
            packageWeight: type.weight || 0,
            packageDimensions: {
              length: type.length || 0,
              width: type.width || 0,
              height: type.height || 0,
              packageType: type.name,
            },
            packageTypeId: type.id,
          });
          const pkg = { ...r.data.package, packageType: type, count };
          setPackages((p) => [...p, pkg]);
          setOpenCount(0);
          setPkgType(null);
          closed = true;
          return parcelLabelResult(pkg);
        } finally {
          // Etykieta nie powstała → backend zostawił paczkę OTWARTĄ (nie zerujemy openCount/typu), więc
          // „Spróbuj ponownie" na ekranie drukowania (albo „Zamknij paczkę") ponawia zakup tej samej
          // przesyłki — UCS ją odtworzy, bez duplikatu. Postęp otwartej paczki zostaje nienaruszony.
          setBusy(false);
        }
      },
      // Po potwierdzeniu (albo ukryciu) wydruku, gdy zostało coś do spakowania → wybór kartonu na
      // następną paczkę.
      onClose: () => { if (closed && itemsLeft) setSheet('pkgtype'); },
    });
  }

  /* ponowienie etykiety dla zamkniętej paczki „bez trackingu" (etykieta nie powstała) */
  function retryLabel(pkg: any) {
    if (!session || busy || !pkg?.packageId) return;
    const packageNumber = packages.indexOf(pkg) + 1;
    startPrint({
      title: shippingLabelTitle,
      subtitle: `${session.orderName} · Paczka ${packageNumber}`,
      preparing: 'Ponawianie przesyłki u przewoźnika…',
      firstStep: 'Przesyłka',
      courier: shipmentCourier,
      addressCheck: addressCheckFor(packageNumber),
      run: async () => {
        setBusy(true);
        try {
          const r = await axios.post(`${API_URL}/api/packing/retry-label`, {
            sessionId: session.sessionId,
            packageId: pkg.packageId,
          });
          const updated = r.data.package;
          setPackages((arr) => arr.map((p) => (p.packageId === pkg.packageId ? { ...p, ...updated } : p)));
          if (!updated?.trackingNumber) throw new Error('Nadal bez numeru śledzenia — spróbuj ponownie');
          return parcelLabelResult({ ...pkg, ...updated });
        } finally {
          setBusy(false);
        }
      },
    });
  }

  // Czy z arkusza wyboru paczki można po prostu wyjść bez utraty postępu?
  // Tak, jeśli typ jest już wybrany lub coś już spakowano/zamknięto. W przeciwnym
  // razie (świeża sesja, nic nie wybrano) zamknięcie = powrót do listy zamówień.
  const canKeepOpen = !!pkgType || openCount > 0 || packages.length > 0;
  function closePkgSheet() {
    if (canKeepOpen) setSheet(null);
    else setSheet('abort');
  }

  function tryFinish() {
    if (openCount > 0) { push('Zamknij otwartą paczkę', 'info'); return; }
    if (packages.length === 0) { push('Najpierw spakuj i zamknij paczkę', 'info'); return; }
    // Etykieta to twardy warunek zakończenia — bez numeru śledzenia przesyłki nie ma, a backend
    // i tak odrzuci complete-order (zamówienie NIE zostanie oznaczone jako spakowane). Blokujemy już
    // tu, by operator dostał jasny komunikat zamiast dialogu „zakończ mimo to" zakończonego błędem.
    const noLabel = packages.filter((p) => !p.trackingNumber);
    if (noLabel.length > 0) {
      AudioFX.buzz();
      const which = noLabel.length === packages.length
        ? 'paczka bez etykiety'
        : `${noLabel.length} z ${packages.length} paczek bez etykiety`;
      push(`Nie można zakończyć — ${which}. Utwórz etykietę (numer śledzenia) przed zakończeniem.`, 'err');
      return;
    }
    setSheet('finish');
  }
  async function confirmFinish() {
    if (!session) return;
    setSheet(null);
    setBusy(true);
    try {
      // Fulfillment (Shopify) realizuje backend w complete-order: jedno wywołanie UCS NA PACZKĘ,
      // z zawartością danej paczki (lineItems) + jej numerem śledzenia → podzielona realizacja w
      // Shopify (widać, co w której paczce, tracking per paczka). Best-effort: gdyby fulfillment
      // się nie powiódł, zamówienie wróci do kolejki /packages/now (UCS dalej je zwraca) — backend
      // zwraca wtedy `fulfillmentError`, który MUSIMY pokazać operatorowi (inaczej myśli, że OK).
      const { data } = await axios.post<{ fulfillmentError?: string | null }>(
        `${API_URL}/api/packing/complete-order`,
        { sessionId: session.sessionId },
      );
      // Zapis wybranego prezentu do rejestru UCS (ADR-0007) — best-effort: błąd grantu nie cofa
      // zakończenia (prezent fizycznie trafił do paczki). Bez kosztów — cenę produkcji rozwiązuje
      // UCS z katalogu po swojej stronie.
      if (giftPick) {
        try {
          await axios.post(`${API_URL}/api/packing/grant-gifts`, {
            sessionId: session.sessionId,
            items: [{
              variantId: giftPick.variantId,
              sku: giftPick.sku,
              title: giftPick.title,
              quantity: 1,
            }],
          });
        } catch {
          /* best-effort — nie blokuje zakończenia */
        }
      }
      endedRef.current = true;
      localStorage.removeItem('currentPackingSession');
      if (data?.fulfillmentError) {
        push(`${session.orderName}: ${data.fulfillmentError}`, 'err');
        AudioFX.buzz();
      } else {
        push(`${session.orderName} zrealizowane`, 'ok');
        AudioFX.success();
      }
      // najpierw nawigacja (odmontowuje ekran), potem czyszczenie sesji —
      // odwrotna kolejnosc wyzwalala efekt wznowienia i przekierowanie na /orders
      navigate('/packing-station/done');
      setSession(null);
    } catch (e: any) {
      push(e?.response?.data?.error || 'Błąd kończenia pakowania', 'err');
    } finally {
      setBusy(false);
    }
  }
  function confirmAbort() {
    endedRef.current = true;
    localStorage.removeItem('currentPackingSession');
    navigate('/packing-station/orders');
    setSession(null);
  }

  function printDoc(kind: 'list' | 'labels') {
    if (!session) return;
    const url = kind === 'list' ? '/api/printing/print-packing-list' : '/api/printing/print-product-labels';
    startPrint({
      title: kind === 'list' ? 'Lista pakowania' : 'Etykiety produktów',
      subtitle: session.orderName,
      unit: kind === 'list' ? 'page' : 'label',
      // sessionId: backend drukuje dokładnie listę z tego ekranu (bez sztuk usuniętych z zamówienia
      // i wysłanych wcześniej), a nie surowe pozycje zamówienia z Shopify.
      run: async () =>
        jobsFrom((await axios.post(`${API_URL}${url}`, { orderId: session.orderId, sessionId: session.sessionId })).data),
    });
  }

  if (!session || !order) return null;

  // Notatka klienta żyje w zamówieniu Shopify z sesji. `order` przy starcie z kolejki to wiersz UCS
  // bez notatki, więc baner „Notatka" nigdy się nie pokazywał — widać go było tylko po odświeżeniu.
  const note = rawText(session.order?.note) || rawText(order.note);

  // Klient w nagłówku. Wiersz kolejki UCS (sourceOrder) nie ma `customer.first_name` ani
  // `shipping_address`, więc bez kontaktu z backendu nagłówek zostawał pusty.
  const customerName = session.contact?.name
    || [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ')
    || order.customer?.name || '';
  const customerCity = session.contact?.city || order.shipping_address?.city || order.customer?.city || '';
  const customerLine = [customerName, customerCity].filter(Boolean).join(' · ');

  // Wybrany prezent jako pozycja „do spakowania": gift badge + licznik 0/1, dotknięcie = spakowany.
  // Nie ma SKU zamówienia, więc nie skanujemy go w backendzie — to czysto wizualna pozycja, która
  // przy finiszu trafia do rejestru darowizn UCS.
  const giftRow = giftPick ? (
    <div
      className={`item-card ${giftPacked ? 'complete' : ''}`}
      style={{ borderColor: 'var(--accent)' }}
      onClick={tapGift}
    >
      <div className="thumb" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
        <Icon name={giftPacked ? 'check' : 'gift'} size={26} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="nm">{giftPick.title}</div>
        <div className="sku" style={{ color: 'var(--accent-text)' }}>
          Prezent dla klienta · {session.gifts?.labelNote || 'GRATIS'}
        </div>
      </div>
      <div className="count">
        <span className={giftPacked ? 'done' : ''}>{giftPacked ? 1 : 0}</span>
        <span className="sep">/</span>
        <span>1</span>
      </div>
    </div>
  ) : null;

  // Pozycje, które już wyszły wcześniejszą realizacją (zamówienie podzielone w Shopify) — zwinięte,
  // wyszarzone, poza licznikiem i skanem. Tylko kontekst: operator wie, że to dosyłka, i nie szuka
  // produktów, które klient już dostał.
  const shipped = session?.fulfilledLineItems || [];
  const shippedList = shipped.length > 0 ? (
    <details style={{ marginTop: 12, color: 'var(--fg-3)' }}>
      <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
        Wysłane wcześniej · {shipped.reduce((s, i) => s + i.quantity, 0)} szt.
      </summary>
      {shipped.map((it) => (
        <div key={`shipped-${it.id}`} className="item-card" style={{ opacity: 0.55, cursor: 'default' }}>
          <div className="thumb"><Icon name="check" size={26} /></div>
          <div style={{ minWidth: 0 }}>
            <div className="nm">{it.title}{it.variant_title ? ` · ${it.variant_title}` : ''}</div>
            <div className="sku">{it.sku || '—'}</div>
          </div>
          <div className="count"><span>{it.quantity}</span></div>
        </div>
      ))}
    </details>
  ) : null;

  const itemList = (
    <>
      {items.map((it) => {
        const complete = it.packed >= it.quantity;
        // Odpiąć można tylko sztuki z bieżącej, otwartej paczki — pozycje zamkniętych paczek mają
        // etykietę i są nieodwracalne (po zamknięciu paczki przycisk znika).
        const canUnpack = openPackedOf(it) > 0;
        return (
          <div key={String(it.id)} className={`item-card ${complete ? 'complete' : ''}`} onClick={() => tapItem(it)}>
            <div className="thumb"><Icon name={complete ? 'check' : 'package'} size={26} /></div>
            <div style={{ minWidth: 0 }}>
              <div className="nm">{it.name}{it.variant_title ? ` · ${it.variant_title}` : ''}</div>
              <div className="sku">{it.sku}</div>
            </div>
            <div className="count">
              <span className={complete ? 'done' : ''}>{it.packed}</span>
              <span className="sep">/</span>
              <span>{it.quantity}</span>
            </div>
            {canUnpack && (
              <button
                type="button"
                className="item-unpack"
                title="Cofnij jedną sztukę z otwartej paczki"
                aria-label={`Cofnij jedną sztukę: ${it.name}`}
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); doUnscan(it); }}
              >
                <Icon name="minus" size={20} />
              </button>
            )}
          </div>
        );
      })}
      {giftRow}
      {shippedList}
    </>
  );

  const packageCol = (
    <div className="sess-col pack scrollable">
      <div className="col-head">Paczki · {packages.length}</div>
      <div className="pkg-list">
        {packages.map((p, i) => (
          <div className="pkg-card" key={i}>
            <div className="ph"><span>Paczka {i + 1} · {p.packageType?.name || '—'}</span>{p.carrier && <CourierBadge courier={courierOfTitle(String(p.carrier))} />}</div>
            <div className="pmeta">{p.packageType ? `${p.packageType.length}×${p.packageType.width}×${p.packageType.height} cm · ` : ''}{p.count || p.items?.length || 0} szt.</div>
            {p.trackingNumber
              ? <div className="ptrack" style={{ color: 'var(--success-text)' }}><Icon name="check-circle-2" size={13} /> {p.trackingNumber}</div>
              : p.labelError
                ? <div className="ptrack" style={{ color: 'var(--danger-text)' }}><Icon name="alert-circle" size={13} /> {p.labelError}</div>
                : <div className="ptrack" style={{ color: 'var(--warning-text)' }}><Icon name="file-spreadsheet" size={13} /> bez trackingu</div>}
            {/* Paczka bez numeru śledzenia (etykieta nie powstała) → ręczne ponowienie. Idempotentne:
                UCS odtwarza tę samą przesyłkę, więc kliknięcie nie kupi duplikatu. */}
            {!p.trackingNumber && (
              <button type="button" className="pkg-retry" disabled={busy} onClick={() => retryLabel(p)}>
                <Icon name="refresh-cw" size={14} /> Ponów etykietę
              </button>
            )}
          </div>
        ))}
        {openCount > 0 && (
          <div className="pkg-card" style={{ borderStyle: 'dashed', borderColor: 'var(--accent)', cursor: 'pointer' }} onClick={() => setSheet('pkgtype')}>
            <div className="ph"><span style={{ color: 'var(--accent-text)' }}>Otwarta paczka{pkgType ? ` · ${pkgType.name}` : ''}</span><Icon name="package" size={16} /></div>
            <div className="pmeta">{openCount} szt. · {pkgType ? 'dotknij, aby zmienić typ' : 'wybierz typ paczki'}</div>
          </div>
        )}
        {packages.length === 0 && openCount === 0 && (
          <div style={{ color: 'var(--fg-3)', fontSize: 13, padding: '8px 4px', textAlign: 'center' }}>Zeskanuj pozycje, potem zamknij paczkę</div>
        )}
      </div>
    </div>
  );

  const scanDock = (
    <div className="scan-dock">
      {pinpad && (
        <PinPad value={scanVal} onChange={setScanVal} onConfirm={pinpadConfirm} onClose={() => setPinpad(false)} />
      )}
      <div className="assist" style={{ margin: 0 }}>
        <Icon name="sparkles" size={17} />
        <span>Asystent sprawdza zgodność skanów. {allPacked ? 'Wszystko spakowane — zamknij paczkę i zakończ.' : `Pozostało ${totalNeeded - totalPacked} szt.`}</span>
      </div>
      <form className="scan-input" onSubmit={onSubmit}>
        <span className="pulse" />
        <input ref={inputRef} value={scanVal} onChange={(e) => setScanVal(e.target.value)} placeholder="Skanuj SKU…" inputMode="numeric" autoFocus={!compact && !sheet && !printing} />
        <button type="button" className={`numpad-toggle ${pinpad ? 'on' : ''}`} aria-pressed={pinpad} onClick={() => (pinpad ? setPinpad(false) : openPinpad())} title="Wpisz SKU numerycznie">123</button>
        <button type="button" className="cam" onClick={scanNext} title="Dodaj następną pozycję"><Icon name="scan-line" size={22} /></button>
      </form>
      {pkgType && (
        <button type="button" className="pkg-pick-chip" onClick={() => setSheet('pkgtype')}>
          <Icon name="package" size={15} />
          <span>Opakowanie: <b>{pkgType.name}</b></span>
          <span className="ppc-change"><Icon name="pencil" size={13} /> zmień</span>
        </button>
      )}
      <div className="scan-actions">
        <button className="big-btn bb-ghost" disabled={busy || openCount === 0} onClick={closePackage}><Icon name="package-plus" size={20} /> Zamknij paczkę</button>
        <button className={`big-btn ${allPacked ? 'bb-success' : 'bb-primary'}`} disabled={busy} onClick={tryFinish}><Icon name="check-check" size={20} /> Zakończ zamówienie</button>
      </div>
    </div>
  );

  const header = (
    <div className="sess-head">
      <button className="back" onClick={() => setSheet('abort')} title="Przerwij"><Icon name="x" size={20} /></button>
      <span className="sess-chip">{session.orderName}</span>
      {session.sourceOrder?.priority?.vip && (
        <span className="sess-chip" style={{ background: 'var(--warning-soft)', color: 'var(--warning-text)' }} title="Klient VIP — priorytet">VIP</span>
      )}
      <div className="sess-prog-wrap">
        <div className="sess-prog-top">
          <b>{customerLine}</b>
          <span className="mono">{totalPacked}/{totalNeeded} · {pct}%</span>
        </div>
        <div className={`kprog ${allPacked ? 'full' : ''}`}><i style={{ width: `${pct}%` }} /></div>
      </div>
      {session.contact && (
        <button className="icon-btn" title="Kontakt z klientem" aria-label="Kontakt z klientem" onClick={() => { AudioFX.click(); setSheet('contact'); }}>
          <Icon name="phone" size={18} />
        </button>
      )}
      {!compact && (
        <>
          <button className="icon-btn" title="Drukuj list pakowania" onClick={() => printDoc('list')}><Icon name="file-text" size={18} /></button>
          <button className="icon-btn" title="Drukuj etykiety produktów" onClick={() => printDoc('labels')}><Icon name="tag" size={18} /></button>
          <button className="icon-btn" title="Widok fokus" onClick={() => setVariant((v) => (v === 'focus' ? 'two-col' : 'focus'))}>
            <Icon name={variant === 'focus' ? 'columns-2' : 'focus'} size={18} />
          </button>
        </>
      )}
      <CourierBadge courier={
        isInpostOrder(order) ? (order?.delivery_info?.type?.startsWith('locker') ? 'paczkomat' : 'inpost')
          : isDhlOrder(order) ? 'dhl'
          : (order?.tags || '').toLowerCase().includes('goodspeed') ? 'goodspeed'
          : order?.delivery_info?.cod ? 'cod' : 'kurier'
      } lg />
    </div>
  );

  let body: React.ReactNode;
  if (compact) {
    body = (
      <div className="sess-body" style={{ flexDirection: 'column' }}>
        <div className="scrollable" style={{ flex: 1 }}>
          {note && <div className="assist" style={{ background: 'var(--warning-soft)', color: 'var(--warning-text)', marginTop: 12 }}><Icon name="sticky-note" size={16} /><span><b>Notatka:</b> {note}</span></div>}
          {itemList}
          <div className="col-head" style={{ marginTop: 8 }}>Paczki · {packages.length}</div>
          <div className="pkg-list">
            {packages.map((p, i) => (
              <div className="pkg-card" key={i}>
                <div className="ph"><span>Paczka {i + 1} · {p.packageType?.name || '—'}</span>{p.carrier && <CourierBadge courier={courierOfTitle(String(p.carrier))} />}</div>
                <div className="pmeta">{p.count || 0} szt.{p.trackingNumber ? ` · ${p.trackingNumber}` : ''}</div>
              </div>
            ))}
            {openCount > 0 && <div className="pkg-card" style={{ borderStyle: 'dashed', borderColor: 'var(--accent)', cursor: 'pointer' }} onClick={() => setSheet('pkgtype')}><div className="pmeta" style={{ color: 'var(--accent-text)' }}>Otwarta: {openCount} szt.{pkgType ? ` · ${pkgType.name} · dotknij, aby zmienić` : ''}</div></div>}
          </div>
        </div>
      </div>
    );
  } else if (variant === 'focus') {
    const next = items.find((i) => i.packed < i.quantity) || items[items.length - 1];
    body = (
      <div className="sess-body" style={{ flexDirection: 'column' }}>
        <div className="scrollable" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {next && (
            <div style={{ flex: '0 0 auto', padding: '24px 24px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
              <div style={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--fg-3)', fontWeight: 600 }}>Następna pozycja</div>
              <div style={{ width: 120, height: 120, borderRadius: 28, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name="package" size={60} /></div>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28, letterSpacing: '-0.02em' }}>{next.name}</div>
                <div className="mono" style={{ color: 'var(--fg-3)', marginTop: 4 }}>{next.sku}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                <span style={{ color: 'var(--accent)' }}>{next.packed}</span>
                <span style={{ color: 'var(--fg-3)', fontSize: 28 }}> / {next.quantity}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 20px 16px', justifyContent: 'center' }}>
            {items.map((it) => (
              <button key={String(it.id)} onClick={() => tapItem(it)}
                style={{ border: '1px solid var(--border)', background: it.packed >= it.quantity ? 'var(--success-soft)' : 'var(--surface)', color: it.packed >= it.quantity ? 'var(--success-text)' : 'var(--fg-2)', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {it.packed >= it.quantity && <Icon name="check" size={13} />}{it.sku.split('-')[0]} {it.packed}/{it.quantity}
              </button>
            ))}
            {giftPick && (
              <button onClick={tapGift}
                style={{ border: '1px solid var(--accent)', background: giftPacked ? 'var(--accent)' : 'var(--accent-soft)', color: giftPacked ? '#fff' : 'var(--accent-text)', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <Icon name="gift" size={13} />{giftPick.title} {giftPacked ? 1 : 0}/1
              </button>
            )}
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="sess-body">
        <div className="sess-col items scrollable">
          {note && <div className="assist" style={{ background: 'var(--warning-soft)', color: 'var(--warning-text)', margin: '12px 16px' }}><Icon name="sticky-note" size={16} /><span><b>Notatka:</b> {note}</span></div>}
          <div className="col-head">Do spakowania<span style={{ color: 'var(--fg-2)' }}>{totalPacked}/{totalNeeded}</span></div>
          {itemList}
        </div>
        {packageCol}
      </div>
    );
  }

  const gifts = session.gifts;
  const giftBanner =
    gifts?.enabled && (gifts.suggestions?.length ?? 0) > 0 ? (
      <div
        className="assist"
        style={{
          background: 'var(--accent-soft)',
          color: 'var(--accent-text)',
          margin: '12px 16px',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
          <Icon name="gift" size={16} />
          <span>Prezent dla klienta</span>
          <span style={{ fontWeight: 400, fontSize: 12 }}>· wybierz 1 produkt</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {gifts.suggestions!.map((s) => {
            const key = s.variantId ?? s.sku ?? s.title;
            const on = giftPick != null && (giftPick.variantId ?? giftPick.sku ?? giftPick.title) === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  AudioFX.click();
                  // Pojedynczy wybór: klik w wybrany odznacza, klik w inny przełącza. Zmiana wyboru
                  // zeruje „spakowany" (nowy prezent czeka na dorzucenie do paczki).
                  if (on) {
                    setGiftPick(null);
                  } else {
                    setGiftPick({ variantId: s.variantId, sku: s.sku, title: s.title });
                  }
                  setGiftPacked(false);
                }}
                style={{
                  border: `2px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent)' : 'transparent',
                  color: on ? '#fff' : 'inherit',
                  borderRadius: 999,
                  padding: '6px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  gap: 6,
                  alignItems: 'center',
                }}
              >
                {on && <Icon name="check" size={13} />}
                {s.title}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <div className="sess">
      {header}
      {giftBanner}
      {body}
      {scanDock}

      {sheet === 'pkgtype' && (
        <PackageTypeSheet
          remaining={Math.max(1, orderNeeded - orderPacked)}
          items={items}
          types={visibleTypes}
          current={pkgType}
          categoryName={orderCategory?.name}
          loading={pickerLoading}
          onPick={pickType}
          onCancel={closePkgSheet}
          cancelLabel={canKeepOpen ? 'Anuluj' : 'Wróć do zamówień'}
        />
      )}
      {sheet === 'contact' && session.contact && (
        <ContactSheet contact={session.contact} orderName={session.orderName} onClose={() => setSheet(null)} />
      )}
      {sheet === 'abort' && (
        <ConfirmDialog
          title="Przerwać sesję pakowania?"
          desc="Postęp zostanie odrzucony (abandon). Tej operacji nie można cofnąć."
          confirmLabel="Tak, przerwij"
          danger
          onConfirm={confirmAbort}
          onCancel={() => setSheet(null)}
        />
      )}
      {sheet === 'finish' && (
        <ConfirmDialog
          title="Zakończyć zamówienie?"
          desc={'Realizacja w Shopify + tag „packed”. Wszystkie paczki zamknięte z etykietą.'}
          confirmLabel="Zakończ zamówienie"
          onConfirm={confirmFinish}
          onCancel={() => setSheet(null)}
        />
      )}
    </div>
  );
}
